import { FilterChangedEvent } from 'ag-grid-community';
import { Injectable } from '@angular/core';
import {
  TaskOverviewFilterValues,
  TasksService,
  User,
} from '@compumark/analyst-selection-tool-client';
import {
  FilterChangeService,
  FilterChip,
  FilterChipStateService,
} from '@compumark/brand-context-components';

import { BehaviorSubject, Observable, filter, first, map } from 'rxjs';
import { TaskFilterModelService } from './task-filter-model.service';
import { UserService } from 'src/app/security/services/user.service';
import { FilterLockService } from 'src/app/results/services/filter-lock.service';
import { PreferenceStorageService } from 'src/app/results/services/preference-storage.service';

@Injectable()
export class TaskFilterChangeService extends FilterChangeService {
  private filterValues$ = new BehaviorSubject<TaskOverviewFilterValues | null>(
    null,
  );
  isGridInitializingObs = new BehaviorSubject<boolean>(false);
  isGridInitialized!: boolean;

  constructor(
    protected filterChipStateService: FilterChipStateService,
    private taskService: TasksService,
    private taskFilterModelService: TaskFilterModelService,
    private userService: UserService,
    private filterLockService: FilterLockService,
    private preferenceService: PreferenceStorageService,
  ) {
    super(filterChipStateService);

    this.taskService
      .getTaskOverviewFilterValues({})
      .pipe(first())
      .subscribe((filterValues) => this.filterValues$.next(filterValues));
  }

  onFilterChange(event: FilterChangedEvent): void {
    this.isGridInitializingObs.subscribe((isInitializing) => {
      this.isGridInitialized = isInitializing;
    });
    const currentFilters = event.api.getFilterModel();
    const filterChips = this.buildFilterChipsFromTableFilters(
      currentFilters,
      event,
    );

    this.filterValues$.next(null);
    this.taskService
      .getTaskOverviewFilterValues(
        this.taskFilterModelService.createTaskFilter(currentFilters),
      )
      .pipe(first())
      .subscribe((filterValues) => this.filterValues$.next(filterValues));

    this.filterLockService.updateLockedChipsList(
      filterChips,
      this.isGridInitialized,
    );

    if (!!filterChips) {
      const filterChipsList =
        this.filterLockService.applyLockOnChips(filterChips);
      this.filterChipStateService.updateTableFilters(filterChipsList);
    }
  }
  getCurrentUserName(): string | undefined {
    const userName = this.userService.getCurrentUser();
    return userName.name;
  }

  toggleLockState($event: FilterChip): void {
    this.filterChipStateService.toggleLockState($event);
    this.filterLockService.updateLockedChipsList(
      this.filterChipStateService.filterChips,
      false,
    );
  }

  public getTaskFilterValues(): Observable<string[]> {
    return this.filterValues$.pipe(
      filter((filterValues) => !!filterValues),
      map((filterValues) => filterValues!.tasks!),
    );
  }

  public getProductFilterValues(): Observable<string[]> {
    return this.filterValues$.pipe(
      filter((filterValues) => !!filterValues),
      map((filterValues) => filterValues!.products!),
    );
  }

  getfilterChipChanges(): Observable<FilterChip[]> {
    return this.filterChipStateService.getfilterChipChanges();
  }

  getLockEnabledFields(): string[] {
    return this.filterLockService.getLockableFields();
  }

  clearChip(filterChip: FilterChip): void {
    this.filterLockService.clearChip(filterChip);
  }

  removeAllFilters(gridapi: any): void {
    const lockedChips = this.filterLockService.getLockedChips();
    const currentFilters = gridapi!.getFilterModel();

    if (lockedChips.length === 0) {
      gridapi!.setFilterModel(null);
      gridapi?.onFilterChanged();
    } else {
      this.filterLockService.removeUnlockedFilterModel(currentFilters);
      gridapi!.setFilterModel(currentFilters);
      gridapi?.onFilterChanged();
    }
  }

  public initializeLockedFilterChips(user: User): void {
    const savedLockedChips = this.preferenceService.getPreference(
      this.filterLockService.getLockFilterPreferenceKey(
        user?.name!,
        'taskOverview',
      ),
    );

    this.filterLockService.initializeLockedChips(
      savedLockedChips,
      user?.name!,
      'taskOverview',
    );
  }
}
